import { Theme } from "@mui/material";
import { ColDef } from "ag-grid-community";
import Enumerable from "linq";
import { LiquidProductionTATEnum } from "../../../common/enums/LiquidProductionTATEnum";
import { UIViewEnum } from "../../../common/enums/UIViewEnum";
import { IBaseGridColumnDefinition } from "../../../common/models/BaseGridColumnDefinition";
import { getTagRoute, getTankBatteryRoute } from "../../../common/utilities/RouteHelpers";
import { RowDef } from "../../../components/ag-grid-extensions/models/RowDef";
import { StandardCellData } from "../../../components/ag-grid-extensions/renderers/StandardCellRenderer";
import { createNumericColumn, createTextColumn } from "../../../components/summary-listing-grid/ColumnCreators";
import { ITankBatterySummaryGridModel, ITankBatterySummaryGridRow } from "../../../services/summary-listing-data-service/TankBatterySummaryModels";

export type TankBatteryRow = RowDef & {
    tankBatteryID: number;
    cell0: StandardCellData;
    cell1: StandardCellData;
    cell2: StandardCellData;
    cell3: StandardCellData;
};

export function createGridData(sourceData: ITankBatterySummaryGridModel | undefined, groupID: number, theme: Theme) {
    const cols: Array<ColDef> = createGridColumns(sourceData, theme);
    const rows: Array<RowDef> = createGridRows(sourceData, groupID);
    const data = cols && rows
        ? {
            rows: rows,
            cols: cols
        } : {
            rows: new Array<RowDef>(),
            cols: new Array<ColDef>()
        };
    return data;
}

export function createGridRows(sourceData: ITankBatterySummaryGridModel | undefined, groupID: number): Array<TankBatteryRow> {
    return sourceData?.rows.map((row: ITankBatterySummaryGridRow) => {
        const cell1 = Enumerable.from(row.tags).firstOrDefault(t => t.tagAssociationType.id === LiquidProductionTATEnum.CapacityFilled);
        const cell2 = Enumerable.from(row.tags).firstOrDefault(t => t.tagAssociationType.id === LiquidProductionTATEnum.DailyOilProduction);
        const cell3 = Enumerable.from(row.tags).firstOrDefault(t => t.tagAssociationType.id === LiquidProductionTATEnum.DailyWaterProduction);

        return {
            tankBatteryID: row.id,
            cell0: {
                displayText: row.displayText,
                href: getTankBatteryRoute(UIViewEnum.TankBattery_Details, groupID, Number(row.id))
            },
            // Capacity Filled (%)
            cell1: {
                displayText: cell1?.displayText ?? "",
                dataValue: cell1?.dataValue,
                href: getTagRoute(UIViewEnum.Tag_Details, groupID, undefined, undefined, row.id, undefined, cell1?.id)
            },
            // Daily Oil Production (Barrels)
            cell2: {
                displayText: cell2?.displayText ?? "",
                dataValue: cell2?.dataValue,
                href: getTagRoute(UIViewEnum.Tag_Details, groupID, undefined, undefined, row.id, undefined, cell2?.id)
            },
            // Daily Water Production (Barrels)
            cell3: {
                displayText: cell3?.displayText ?? "",
                dataValue: cell3?.dataValue,
                href: getTagRoute(UIViewEnum.Tag_Details, groupID, undefined, undefined, row.id, undefined, cell3?.id)
            }
        };

    }) ?? new Array<TankBatteryRow>();
}

export function createGridColumns(sourceData: ITankBatterySummaryGridModel | undefined, theme: Theme): Array<ColDef> {

    return sourceData?.columns.map((col: IBaseGridColumnDefinition, index: number) => {

        switch (index) {
            case 0:
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText, initialSort: "asc", initialSortIndex: 0 });
            default:
                return createNumericColumn({ field: `cell${index}`, headerName: col.headerText });
        }
    }) ?? new Array<ColDef>();
}
